// Mock data for the moving company application

export const mockData = {
  // Calendar pricing
  pricing: {
    weekday: 125,
    weekend: 145
  },

  // Property types and sizes
  propertyTypes: {
    house: {
      sizes: [
        { range: '500-800', minSqft: 500, maxSqft: 800 },
        { range: '900-1200', minSqft: 900, maxSqft: 1200 },
        { range: '1200-1600', minSqft: 1200, maxSqft: 1600 },
        { range: '1700-2000', minSqft: 1700, maxSqft: 2000 },
        { range: '2100-2500', minSqft: 2100, maxSqft: 2500 },
        { range: '2600-3000', minSqft: 2600, maxSqft: 3000 },
        { range: '3000-4000', minSqft: 3000, maxSqft: 4000 },
        { range: '4000-5000', minSqft: 4000, maxSqft: 5000 }
      ]
    },
    apartment: {
      sizes: [
        { range: '400-600', minSqft: 400, maxSqft: 600 },
        { range: '600-800', minSqft: 600, maxSqft: 800 },
        { range: '800-1200', minSqft: 800, maxSqft: 1200 },
        { range: '1200-1600', minSqft: 1200, maxSqft: 1600 },
        { range: '1600-2000', minSqft: 1600, maxSqft: 2000 },
        { range: '2000-2500', minSqft: 2000, maxSqft: 2500 }
      ]
    },
    warehouse: {
      sizes: [
        { range: '1000-2000', minSqft: 1000, maxSqft: 2000 },
        { range: '2000-5000', minSqft: 2000, maxSqft: 5000 },
        { range: '5000-10000', minSqft: 5000, maxSqft: 10000 },
        { range: '10000+', minSqft: 10000, maxSqft: 999999 }
      ]
    }
  },

  // Crew size calculation logic - ALL CREWS ARE MINIMUM 3-MAN
  getCrewRecommendation: (propertyType, sizeRange, floor, hasElevator) => {
    const size = mockData.propertyTypes[propertyType]?.sizes.find(s => s.range === sizeRange);
    if (!size) return { men: 3, trucks: 1, callCompany: false };

    const maxSqft = size.maxSqft;

    // Special handling for warehouses - always call company for complex moves
    if (propertyType === 'warehouse' && maxSqft >= 5000) {
      return { men: 0, trucks: 0, callCompany: true };
    }

    // For very large properties, recommend calling company
    if (maxSqft >= 4000) {
      return { men: 0, trucks: 0, callCompany: true };
    }

    // Special rule: Elevator access always gets 3-man crew minimum
    if (floor === 'elevator') {
      if (maxSqft >= 1900) {
        return { men: 4, trucks: 2, callCompany: false };
      } else {
        return { men: 3, trucks: 1, callCompany: false };
      }
    }

    // Updated logic for HOUSES: ALL HOUSES GET MINIMUM 3-MAN CREW
    if (propertyType === 'house') {
      if (maxSqft < 900) {
        return { men: 3, trucks: 1, callCompany: false };
      } else if (maxSqft >= 900 && maxSqft <= 1800) {
        return { men: 3, trucks: 1, callCompany: false };
      } else if (maxSqft >= 1900) {
        return { men: 4, trucks: 2, callCompany: false };
      }
    }

    // Special rule for apartments: 2nd or 3rd floor always gets 3-man crew
    if (propertyType === 'apartment' && (floor === 'second' || floor === 'third')) {
      if (maxSqft >= 1900) {
        return { men: 4, trucks: 2, callCompany: false };
      } else {
        return { men: 3, trucks: 1, callCompany: false };
      }
    }

    // Standard logic for apartments and other property types - ALL GET MINIMUM 3-MAN CREW
    if (maxSqft < 1200 && floor === 'first') {
      return { men: 3, trucks: 1, callCompany: false };
    } else if (maxSqft >= 1200 && maxSqft <= 1800) {
      return { men: 3, trucks: 1, callCompany: false };
    } else if (maxSqft >= 1900) {
      return { men: 4, trucks: 2, callCompany: false };
    }

    return { men: 3, trucks: 1, callCompany: false };
  },

  // Mock Google Maps data
  mockRouteData: {
    distance: '12.5 miles',
    duration: '18 minutes',
    mapImageUrl: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=800&h=400&fit=crop',
    route: [
      { lat: 40.7128, lng: -74.0060 },
      { lat: 40.7589, lng: -73.9851 }
    ]
  },

  // Time estimation logic (mock) - Updated for double truck pricing
  estimateMovingTime: (propertySize, crewSize, floors, distance, trucks = 1) => {
    const baseTime = {
      '500-800': 2,
      '900-1200': 3,
      '1200-1600': 4,
      '1700-2000': 5,
      '2100-2500': 6,
      '2600-3000': 7,
      '3000-4000': 8,
      '4000-5000': 10
    };

    let estimatedHours = baseTime[propertySize] || 4;
    
    // Adjust for floors (if no elevator)
    if (floors !== 'first' && floors !== 'elevator') {
      const floorMultiplier = {
        'second': 1.3,
        'third': 1.5
      };
      estimatedHours *= floorMultiplier[floors] || 1.3;
    }

    // Adjust for crew size (more crew = faster)
    if (crewSize >= 4) {
      estimatedHours *= 0.8;
    } else if (crewSize === 2) {
      estimatedHours *= 1.2;
    }

    // If 2 trucks are required, the time might be slightly reduced due to efficiency
    // but the cost should be doubled (handled in pricing logic)
    if (trucks === 2) {
      estimatedHours *= 0.9; // Slight time reduction for efficiency
    }

    // Add travel time based on distance
    const travelTime = parseFloat(distance) * 0.1; // Rough estimate
    estimatedHours += travelTime;

    return Math.max(2, Math.round(estimatedHours * 10) / 10); // Minimum 2 hours
  },

  // Calculate total cost including travel fee based on distance
  calculateTotalCost: (hourlyRate, estimatedHours, trucks, additionalCosts = 0, distanceInMiles = 0) => {
    const baseMovingCost = estimatedHours * hourlyRate;
    
    // If 2 trucks are required, double the base cost
    const truckMultiplier = trucks === 2 ? 2 : 1;
    const totalMovingCost = baseMovingCost * truckMultiplier;
    
    // Calculate travel fee based on distance
    // Under 30 miles: $65 flat fee
    // Over 30 miles: $65 + $10 for every 10 miles over 30
    let travelFee = 65; // Base fee for up to 30 miles
    
    if (distanceInMiles > 30) {
      const additionalMiles = distanceInMiles - 30;
      const additional10MileIncrements = Math.ceil(additionalMiles / 10);
      travelFee = 65 + (additional10MileIncrements * 10);
    }
    
    return {
      baseMovingCost,
      truckMultiplier,
      totalMovingCost,
      travelFee,
      additionalCosts,
      grandTotal: totalMovingCost + travelFee + additionalCosts
    };
  }
};

export default mockData;